import axios, { type AxiosRequestConfig } from 'axios'

import { env } from '@/config/env'
import { getCurrentLanguage, i18n } from '@/i18n'

import {
  createApiErrorFromResponse,
  createUnexpectedResponseError,
  isApiErrorResponse,
  unwrapPageResult,
  unwrapSuccessData,
} from './envelope'
import { ApiError, ClientErrorCode, isApiError } from './errors'
import { type PageQuery, type PageResult, type RequestOptions, ShowType } from './types'

/** Header names must match Tokki-backend internal/middleware. */
const REQUEST_ID_HEADER = 'X-Request-ID'
const LANGUAGE_HEADER = 'X-Language'
const AUTHORIZATION_HEADER = 'Authorization'

const HTTP_STATUS_NO_CONTENT = 204

type AuthorizationProvider = () => string | undefined

let authorizationProvider: AuthorizationProvider = () => undefined

/**
 * Registers how the `Authorization` header is built. Called once at startup by the Telegram layer
 * so this module never depends on the Telegram SDK directly.
 */
export function setAuthorizationProvider(provider: AuthorizationProvider): void {
  authorizationProvider = provider
}

function createRequestId(): string {
  // randomUUID only exists in secure contexts; plain-HTTP LAN debugging falls back to random bytes.
  if (typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  const randomBytes = crypto.getRandomValues(new Uint8Array(16))
  return Array.from(randomBytes, (randomByte) => randomByte.toString(16).padStart(2, '0')).join('')
}

const axiosInstance = axios.create({
  baseURL: env.apiBaseUrl,
  timeout: env.requestTimeoutMs,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
})

axiosInstance.interceptors.request.use((requestConfig) => {
  requestConfig.headers.set(LANGUAGE_HEADER, getCurrentLanguage())
  if (!requestConfig.headers.has(REQUEST_ID_HEADER)) {
    requestConfig.headers.set(REQUEST_ID_HEADER, createRequestId())
  }
  const authorizationValue = authorizationProvider()
  if (authorizationValue && !requestConfig.headers.has(AUTHORIZATION_HEADER)) {
    requestConfig.headers.set(AUTHORIZATION_HEADER, authorizationValue)
  }
  return requestConfig
})

function createClientError(errorCode: ClientErrorCode, message: string, cause: unknown): ApiError {
  return new ApiError({ errorCode, message, showType: ShowType.ErrorMessage, cause })
}

/** Converts every failure (backend envelope, timeout, offline, cancel) into one `ApiError` shape. */
export function toApiError(error: unknown): ApiError {
  if (isApiError(error)) {
    return error
  }
  if (axios.isCancel(error)) {
    return new ApiError({
      errorCode: ClientErrorCode.Canceled,
      message: 'Request canceled',
      showType: ShowType.Silent,
      cause: error,
    })
  }
  if (axios.isAxiosError(error)) {
    const httpStatus = error.response?.status
    const responsePayload: unknown = error.response?.data
    if (isApiErrorResponse(responsePayload)) {
      return createApiErrorFromResponse(responsePayload, httpStatus)
    }
    const isTimeout = error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT'
    if (isTimeout) {
      return createClientError(ClientErrorCode.Timeout, i18n.t('error.timeout'), error)
    }
    if (!error.response) {
      return createClientError(ClientErrorCode.NetworkError, i18n.t('error.network'), error)
    }
    return createUnexpectedResponseError(httpStatus, responsePayload)
  }
  return createClientError(ClientErrorCode.Unknown, i18n.t('error.unknown'), error)
}

function toAxiosConfig(options: RequestOptions | undefined): AxiosRequestConfig {
  return {
    params: options?.params,
    headers: options?.headers,
    signal: options?.signal,
    timeout: options?.timeoutMs,
  }
}

interface RawResponse {
  payload: unknown
  httpStatus: number
}

async function sendRequest(requestConfig: AxiosRequestConfig): Promise<RawResponse> {
  try {
    const response = await axiosInstance.request<unknown>(requestConfig)
    return { payload: response.data, httpStatus: response.status }
  } catch (error) {
    throw toApiError(error)
  }
}

async function requestData<Data>(requestConfig: AxiosRequestConfig): Promise<Data> {
  const { payload, httpStatus } = await sendRequest(requestConfig)
  if (httpStatus === HTTP_STATUS_NO_CONTENT) {
    return undefined as Data
  }
  return unwrapSuccessData<Data>(payload, httpStatus)
}

/**
 * The only way business code talks to Tokki-backend. Every method resolves with the unwrapped
 * `data` field and rejects with `ApiError`.
 */
export const http = {
  get<Data>(url: string, options?: RequestOptions): Promise<Data> {
    return requestData<Data>({ ...toAxiosConfig(options), url, method: 'GET' })
  },

  async getPage<Item>(
    url: string,
    pageQuery?: PageQuery,
    options?: RequestOptions,
  ): Promise<PageResult<Item>> {
    const { payload, httpStatus } = await sendRequest({
      ...toAxiosConfig(options),
      url,
      method: 'GET',
      params: { ...options?.params, ...pageQuery },
    })
    return unwrapPageResult<Item>(payload, httpStatus)
  },

  post<Data, Body = unknown>(url: string, body?: Body, options?: RequestOptions): Promise<Data> {
    return requestData<Data>({ ...toAxiosConfig(options), url, method: 'POST', data: body })
  },

  put<Data, Body = unknown>(url: string, body?: Body, options?: RequestOptions): Promise<Data> {
    return requestData<Data>({ ...toAxiosConfig(options), url, method: 'PUT', data: body })
  },

  patch<Data, Body = unknown>(url: string, body?: Body, options?: RequestOptions): Promise<Data> {
    return requestData<Data>({ ...toAxiosConfig(options), url, method: 'PATCH', data: body })
  },

  delete<Data = void>(url: string, options?: RequestOptions): Promise<Data> {
    return requestData<Data>({ ...toAxiosConfig(options), url, method: 'DELETE' })
  },
}
